import { Head, Link, router } from "@inertiajs/react"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ShieldAlert,
  UserCircle,
} from "lucide-react"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { ListPagination } from "@/components/admin/list-pagination"
import { ListShell } from "@/components/admin/list-shell"
import { ListToolbar } from "@/components/admin/list-toolbar"
import { EmptyState } from "@/components/empty-state"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { adminCreatorPath, adminCreatorsPath } from "@/routes"
import type { CreatorsIndexProps } from "@/types/admin"

type SortableColumn = "name" | "tiktok_handle" | "created_at"

/**
 * Admin/Creators/Index — paginated creator list (OKS-15 AC4).
 *
 * Adds a `blocklisted` filter (Backend allows `""` / `"true"`) plus a
 * `tiktok_handle` sortable column (Backend's CreatorsController sortable
 * allowlist is `%w[name tiktok_handle created_at]`).
 *
 * The blocklisted indicator on each row uses `shield-alert` + a red
 * "Blocklisted" pill — icon + text + colour per UX Spec §5
 * accessibility rule. Locked v6 testid: `admin-creator-blocklist-flag`.
 */
export default function AdminCreatorsIndex({
  creators,
  pagination,
  filters,
  sort,
}: CreatorsIndexProps) {
  const { t, i18n } = useTranslation()

  // `filters.blocklisted` is `boolean | null` (Backend's `parse_blocklisted`
  // returns `nil` when the param is absent, `true` for "true"/"1"/"yes",
  // `false` otherwise). An "active" blocklisted filter is anything other
  // than `null` — both `true` (blocklisted only) and `false` (non-
  // blocklisted only) count as user-applied filters per Backend's
  // `unless blocklisted.nil?` filter gate.
  const hasActiveFilters =
    filters.q.length > 0 ||
    filters.created_from.length > 0 ||
    filters.created_to.length > 0 ||
    filters.blocklisted !== null
  const isEmpty = creators.length === 0

  const visit = useCallback(
    (
      overrides: Partial<{
        q: string
        page: number
        sort_by: string
        sort_dir: string
        /**
         * Transport: `"true"` | `""`. `""` means "drop the param" — Backend's
         * `parse_blocklisted` checks `params.key?(:blocklisted)` so absent
         * key resolves to `nil` (unfiltered).
         */
        blocklisted: string
      }>,
    ) => {
      const merged: Record<string, string | number> = {
        q: filters.q,
        created_from: filters.created_from,
        created_to: filters.created_to,
        // Re-serialise the boolean filter to its query-string transport
        // form. `null → ""` (filter absent), `true → "true"` (filter on),
        // `false → "false"` (less common — explicit "non-blocklisted").
        blocklisted:
          filters.blocklisted === null
            ? ""
            : filters.blocklisted
              ? "true"
              : "false",
        sort_by: sort.by,
        sort_dir: sort.dir,
        page: pagination.page,
        ...overrides,
      }
      const clean: Record<string, string | number> = {}
      for (const [k, v] of Object.entries(merged)) {
        if (v !== "" && v !== null && v !== undefined) clean[k] = v
      }
      router.get(adminCreatorsPath(), clean, {
        preserveState: true,
        preserveScroll: true,
        only: ["creators", "pagination", "filters", "sort"],
      })
    },
    [
      filters.q,
      filters.created_from,
      filters.created_to,
      filters.blocklisted,
      sort.by,
      sort.dir,
      pagination.page,
    ],
  )

  const onSearch = useCallback((q: string) => visit({ q, page: 1 }), [visit])
  const onPageChange = useCallback((page: number) => visit({ page }), [visit])
  const onSortToggle = useCallback(
    (column: SortableColumn) => {
      const nextDir = sort.by === column && sort.dir === "desc" ? "asc" : "desc"
      visit({ sort_by: column, sort_dir: nextDir, page: 1 })
    },
    [sort.by, sort.dir, visit],
  )
  const onBlocklistedChange = useCallback(
    (value: string) =>
      visit({ blocklisted: value === "all" ? "" : value, page: 1 }),
    [visit],
  )
  const onClearFilters = useCallback(() => {
    router.get(
      adminCreatorsPath(),
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["creators", "pagination", "filters", "sort"],
      },
    )
  }, [])

  const dateFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  return (
    <AppLayout>
      <Head title={t("admin.creators_list.title")} />
      <ListShell
        testId="admin-creators-list"
        title={t("admin.creators_list.title")}
        subtitle={t("admin.creators_list.subtitle_total", {
          count: pagination.total,
        })}
        toolbar={
          <ListToolbar
            searchValue={filters.q}
            onSearchChange={onSearch}
            filters={
              <Select
                items={{
                  all: t("admin.creators_list.filter.all"),
                  true: t("admin.creators_list.filter.blocklisted_only"),
                }}
                value={filters.blocklisted === true ? "true" : "all"}
                onValueChange={(value) =>
                  value != null && onBlocklistedChange(value)
                }
              >
                <SelectTrigger
                  className="w-[200px]"
                  data-testid="admin-list-filter-blocklisted"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("admin.creators_list.filter.all")}
                  </SelectItem>
                  <SelectItem value="true">
                    {t("admin.creators_list.filter.blocklisted_only")}
                  </SelectItem>
                </SelectContent>
              </Select>
            }
          />
        }
        footer={
          <ListPagination
            page={pagination.page}
            perPage={pagination.per_page}
            total={pagination.total}
            onPageChange={onPageChange}
          />
        }
      >
        {isEmpty ? (
          hasActiveFilters ? (
            <EmptyState
              data-testid="admin-list-empty"
              title={t("admin.list.empty_filter_creators")}
              actions={
                <Button
                  variant="outline"
                  onClick={onClearFilters}
                  data-testid="admin-list-empty-clear"
                >
                  {t("admin.shared.clear_filters")}
                </Button>
              }
            />
          ) : (
            <EmptyState
              data-testid="admin-list-empty"
              icon={UserCircle}
              title={t("admin.list.empty_fresh_creators")}
            />
          )
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <SortHeader
                    column="tiktok_handle"
                    label={t("admin.creators_list.column.handle")}
                    sort={sort}
                    onToggle={onSortToggle}
                  />
                </TableHead>
                <TableHead>
                  <SortHeader
                    column="name"
                    label={t("admin.creators_list.column.name")}
                    sort={sort}
                    onToggle={onSortToggle}
                  />
                </TableHead>
                <TableHead className="text-right">
                  {t("admin.creators_list.column.memberships")}
                </TableHead>
                <TableHead>
                  <SortHeader
                    column="created_at"
                    label={t("admin.creators_list.column.created_at")}
                    sort={sort}
                    onToggle={onSortToggle}
                  />
                </TableHead>
                <TableHead>
                  {t("admin.creators_list.column.blocklist")}
                </TableHead>
                <TableHead>
                  <span className="sr-only">
                    {t("admin.creators_list.column.view")}
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {creators.map((creator) => (
                <TableRow
                  key={creator.id}
                  data-testid="admin-list-row"
                  data-creator-id={creator.id}
                >
                  <TableCell className="font-medium">
                    {creator.tiktok_handle ? (
                      `@${creator.tiktok_handle}`
                    ) : (
                      <span className="text-muted-foreground">
                        {t("admin.creators_list.no_handle")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{creator.name}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {creator.memberships_count}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {dateFmt.format(new Date(creator.created_at))}
                  </TableCell>
                  <TableCell>
                    {creator.blocklisted && (
                      <Badge
                        variant="destructive"
                        className="inline-flex items-center gap-1"
                        data-testid="admin-creator-blocklist-flag"
                      >
                        <ShieldAlert className="size-3" aria-hidden="true" />
                        {t("admin.creators_list.blocklisted_badge")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Link
                      href={adminCreatorPath({ id: creator.id })}
                      className="text-foreground hover:text-foreground/80 text-sm font-medium underline-offset-4 hover:underline"
                      aria-label={t("admin.creators_list.view_label", {
                        name: creator.name,
                      })}
                    >
                      {t("admin.creators_list.column.view")}
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListShell>
    </AppLayout>
  )
}

interface SortHeaderProps {
  column: SortableColumn
  label: string
  sort: { by: string; dir: "asc" | "desc" }
  onToggle: (column: SortableColumn) => void
}

function SortHeader({ column, label, sort, onToggle }: SortHeaderProps) {
  const { t } = useTranslation()
  const isActive = sort.by === column
  const Icon = isActive
    ? sort.dir === "asc"
      ? ArrowUp
      : ArrowDown
    : ArrowUpDown
  return (
    <button
      type="button"
      onClick={() => onToggle(column)}
      className="text-foreground hover:text-foreground/80 inline-flex items-center gap-1 text-xs font-medium tracking-wide uppercase"
      aria-label={t("admin.brands_list.sort.toggle_label", { column: label })}
      aria-sort={
        isActive ? (sort.dir === "asc" ? "ascending" : "descending") : "none"
      }
      data-testid={`admin-list-sort-${column}`}
    >
      <span>{label}</span>
      <Icon className="size-3.5" aria-hidden="true" />
    </button>
  )
}
